/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { memoizedFetchSitePersonalization } from '@jira/platform__services-personalization/src/services/site/index.tsx';
import type { Trait } from '@jira/platform__services-personalization/src/types.tsx';
import type { CrossJoinTargetProduct } from '../../common/types.tsx';

const ACTIVATION_TRAITS: Record<CrossJoinTargetProduct, string> = {
	confluence: 'confluence.ondemand.activationDate',
	'jira-product-discovery': 'jira-product-discovery.activationDate',
	loom: 'loom.activationDate',
};
export const isSite7DaysOld = async (
	product: CrossJoinTargetProduct,
	cloudId: string,
): Promise<boolean> => {
	const siteTraits = await memoizedFetchSitePersonalization({ cloudId });
	return findAndCheckActivationDate(product, siteTraits);
};

const findAndCheckActivationDate = (product: CrossJoinTargetProduct, traits: Trait[]): boolean => {
	const traitName = ACTIVATION_TRAITS[product] || undefined;
	if (!traitName) {
		throw Error(`Unknown activation trait key for site product ${product}`);
	}

	const activationTrait = traits.find((trait) => trait.name === traitName);

	if (activationTrait?.value) {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		const oneWeekLaterActivation = new Date(activationTrait?.value as string);
		oneWeekLaterActivation.setDate(oneWeekLaterActivation.getDate() + 7);
		const today = new Date();
		return today >= oneWeekLaterActivation;
	}
	return false;
};

export const isLoomSite7DaysOld = async (cloudId: string): Promise<boolean> => {
	const siteTraits = await memoizedFetchSitePersonalization({ cloudId });
	return findAndCheckLoomActivationDate(siteTraits);
};

// The loom.activationDate trait is registered on 23rd Aug 2025, any values that are not found within this trait means the site is older than 7 days
// The trait is registered when a new loom site is provisioned
// See https://data-portal.internal.atlassian.com/targeting/traits/63974e01-9d13-4587-8b23-235087258799
const findAndCheckLoomActivationDate = (traits: Trait[]): boolean => {
	const traitName = ACTIVATION_TRAITS.loom;
	if (!traitName) {
		throw Error('Unknown activation trait key for loom site');
	}

	const activationTrait = traits.find((trait) => trait.name === traitName);

	if (activationTrait?.value) {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		const oneWeekLaterActivation = new Date(activationTrait?.value as string);
		oneWeekLaterActivation.setDate(oneWeekLaterActivation.getDate() + 7);
		const today = new Date();
		return today >= oneWeekLaterActivation;
	}
	return true;
};
